import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@syncanix/shared';
import { lazy, Suspense } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import { stripLocalePrefix } from '@/i18n/locale-path';
import { SkipLink } from '@/lib/a11y/SkipLink';

import { LocaleSync } from './core/LocaleSync';
import { ScrollManager } from './core/ScrollManager';
import { ErrorBoundary } from './features/error';
import { Footer } from './features/footer';
import { Header } from './features/header';
import { LandingPage } from './features/landing';
import { NotFound } from './pages/not-found';
import { RouteFallback } from './RouteFallback';
import { LAZY_ROUTES } from './routes';

import type { ReactElement } from 'react';

const {
  features: Features,
  howItWorks: HowItWorks,
  about: About,
  pricing: Pricing,
  pricingHistory: PricingHistory,
  buildVsBuy: BuildVsBuy,
  contact: Contact,
  benchmarks: Benchmarks,
  status: Status,
  docsIndex: DocsIndex,
  docPage: DocPage,
  onePagersIndex: OnePagersIndex,
  onePagerPage: OnePagerPage,
  vsIndex: VsIndex,
  vsPage: VsPage,
  trustHub: TrustHub,
  subprocessors: Subprocessors,
  compliance: Compliance,
  security: Security,
  disclosure: Disclosure,
  accessibility: Accessibility,
  questionnaire: Questionnaire,
  privacy: Privacy,
  aiCompliance: AiCompliance,
  legal: Legal,
  dpa: Dpa,
  terms: Terms,
  cookies: Cookies,
  refunds: Refunds,
} = LAZY_ROUTES;

/**
 * The whole site's route table, declared ONCE (GA-13 / ADR-0133). It is rendered
 * twice: at the site root (English, the unprefixed `x-default`) and again nested
 * under a validated `/:locale` parent (`/es/…`, `/fr/…`, …). Declaring it once
 * keeps the English and per-locale URL trees provably identical — a route can
 * never exist in one but not the other. `path` is the English-relative pattern;
 * `/` becomes the parent's index route under `/:locale`.
 */
const ROUTE_DEFS: readonly { readonly path: string; readonly element: ReactElement }[] = [
  { path: '/', element: <LandingPage /> },
  { path: '/features', element: <Features /> },
  { path: '/how-it-works', element: <HowItWorks /> },
  { path: '/about', element: <About /> },
  { path: '/pricing', element: <Pricing /> },
  { path: '/pricing/history', element: <PricingHistory /> },
  { path: '/build-vs-buy', element: <BuildVsBuy /> },
  // The page used to live under /pricing and is linked from there, from the
  // pricing footer nav, and from anywhere a reader has bookmarked it. The old
  // path keeps rendering rather than 404ing or client-redirecting: a prerendered
  // redirect ships an empty document to a crawler, which is worse for the page
  // than the duplicate it avoids. `BuildVsBuy` emits a canonical pointing at
  // /build-vs-buy from BOTH paths, and only the new one is in the sitemap, so
  // search consolidates on it.
  { path: '/pricing/build-vs-buy', element: <BuildVsBuy /> },
  { path: '/contact', element: <Contact /> },
  { path: '/benchmarks', element: <Benchmarks /> },
  { path: '/status', element: <Status /> },
  { path: '/docs', element: <DocsIndex /> },
  { path: '/docs/*', element: <DocPage /> },
  { path: '/one-pagers', element: <OnePagersIndex /> },
  { path: '/one-pagers/:icp', element: <OnePagerPage /> },
  { path: '/vs', element: <VsIndex /> },
  { path: '/vs/:rival', element: <VsPage /> },
  { path: '/trust', element: <TrustHub /> },
  { path: '/trust/subprocessors', element: <Subprocessors /> },
  { path: '/trust/compliance', element: <Compliance /> },
  { path: '/trust/security', element: <Security /> },
  { path: '/trust/disclosure', element: <Disclosure /> },
  { path: '/trust/accessibility', element: <Accessibility /> },
  { path: '/trust/security-questionnaire', element: <Questionnaire /> },
  { path: '/trust/privacy', element: <Privacy /> },
  { path: '/trust/ai', element: <AiCompliance /> },
  { path: '/trust/dpa', element: <Dpa /> },
  { path: '/legal', element: <Legal /> },
  { path: '/terms', element: <Terms /> },
  { path: '/cookies', element: <Cookies /> },
  { path: '/trust/refunds', element: <Refunds /> },
];

/**
 * Every path pattern the router serves, exported for the route ↔ SEO-SOT
 * reverse-drift guard in `App.test.tsx` (T0.2): a route that exists here but is
 * missing from `site-routes.ts` is invisible to the prerender, the sitemap, and
 * every hreflang alternate — the regression class ADR-0465's removal was checked against.
 */
export const ROUTE_DEF_PATHS: readonly string[] = ROUTE_DEFS.map((r) => r.path);

/**
 * The non-English locale prefixes (`es`, `fr`, `de`, `ar`, `he`). English is the
 * unprefixed `x-default`, so it gets no prefix and `/en/…` is never emitted.
 * Rendering one explicit static parent route per locale (rather than a dynamic
 * `/:locale`) means a non-locale first segment like `docs` or `trust` can never
 * be mistaken for a locale, so `/docs/trust/privacy` resolves to the docs page,
 * never to `:locale=docs`.
 */
const NON_EN_LOCALES: readonly string[] = SUPPORTED_LOCALES.map((l) => l.code).filter(
  (code) => code !== DEFAULT_LOCALE,
);

/**
 * Dev-only demo studio (`/studio`). A full-bleed, no-chrome cinematic used ONLY
 * to author marketing/demo videos — it is NOT part of the public site. Gated
 * behind `import.meta.env.DEV`, so it is dead-code-eliminated from the prod
 * build (the `import()` sits in a provably-false branch), never prerendered
 * (absent from the `site-routes` SOT + sitemap), and `/studio` resolves to the
 * 404 in production. Reachable only under `pnpm dev` for manual recording.
 */
const DemoStudio = import.meta.env.DEV
  ? lazy(() => import('./features/demo-studio/DemoStudio').then((m) => ({ default: m.DemoStudio })))
  : null;

/**
 * Marketing site router shell. Phase 6 Pass 3 iter 242 added the
 * react-router-dom foundation; Phase 7 Pass 1 iter 269 added the
 * site-wide `<Footer />`; Phase 9 added the site-wide `<Header />`;
 * GA-13 (ADR-0133) generalized routing to per-locale URLs site-wide.
 *
 * **Code splitting (launch-readiness B2):** the landing page (the LCP-critical
 * home) and the 404 stay eager; every other route is a lazy chunk from
 * `./routes` — see that module for the SSG-preload + hydration contract.
 *
 * **Unknown paths**: render the iter-295 `<NotFound />` page rather
 * than silently redirecting to home.
 */
export function App(): ReactElement {
  const { pathname } = useLocation();

  // Dev-only `/studio` — bypass ALL site chrome (nav/footer/routes) and render
  // the full-bleed cinematic directly. Never active in prod (DemoStudio is null).
  if (DemoStudio && stripLocalePrefix(pathname).replace(/\/+$/, '') === '/studio') {
    return (
      <Suspense fallback={null}>
        <DemoStudio />
      </Suspense>
    );
  }

  return (
    <>
      <ScrollManager />
      <LocaleSync />
      <SkipLink />
      {/* The home route used to suppress this chrome and render its own dark
          nav + footer. The redesign turned the landing light and folded it onto
          the shared design system, so every route — home included — now carries
          ONE header and ONE footer. That removes the two-chromes-to-keep-in-step
          problem the `chrome-links` SOT existed to paper over, and removes the
          visual seam a visitor crossed on their first click off the landing. */}
      <Header />
      <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {ROUTE_DEFS.map((r) => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}
            {/* The same table mirrored under each non-English locale prefix (ADR-0133). */}
            {NON_EN_LOCALES.map((loc) => (
              <Route key={loc} path={loc}>
                {ROUTE_DEFS.map((r) =>
                  r.path === '/' ? (
                    <Route key={`${loc}-index`} index element={r.element} />
                  ) : (
                    <Route
                      key={`${loc}-${r.path}`}
                      path={r.path.replace(/^\//, '')}
                      element={r.element}
                    />
                  ),
                )}
                {/* Unknown sub-path under a valid locale → localized 404. */}
                <Route path="*" element={<NotFound />} />
              </Route>
            ))}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      <Footer />
    </>
  );
}
